// src/components/CourseCard.jsx

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaCheck } from "react-icons/fa";
import Dialog from "../../dialogs";
import { baseAxios } from "../../../apis";
import { useCsrfToken } from "../../../hooks/useCsrfToken";
import useAuthedApi from "../../../hooks/useAuthedApi";

// Helper component for rendering status and "NEW" badges
const CourseStatus = ({ status, createdAt, duration, contentType }) => {
  const isNew = (() => {
    if (!createdAt) return false;
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(new Date().getDate() - 7);
    return new Date(createdAt) >= oneWeekAgo;
  })();

  const durationText = `${duration || 0} Min${
    Number(duration) !== 1 ? "s" : ""
  }`;
  const durationBadge = (
    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded-md ring-1 ring-blue-400">
      {durationText}
    </span>
  );

  if (!!status && !status?.completed && contentType !== "exam") {
    return (
      <div className="inline-flex items-center bg-yellow-100 text-yellow-800 text-xs font-semibold px-3 py-1 rounded-md ring-1">
        <span className="h-2 w-2 mr-2 bg-yellow-500 rounded-full"></span>
        On Going
      </div>
    );
  }

  if (!!status?.completed) {
    return (
      <div className="inline-flex items-center bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-md ring-1 ring-[#2D4E30]">
        <FaCheck className="mr-2" />
        Finish
      </div>
    );
  }

  if (isNew) {
    return (
      <div className="flex items-center space-x-2">
        {durationBadge}
        <span className="bg-red-100 text-red-800 text-xs font-semibold px-2.5 py-0.5 rounded-md ring-1 ring-red-400">
          NEW
        </span>
      </div>
    );
  }

  return durationBadge;

  // switch (status) {
  //   case "On Going":
  //     return (
  //       <div className="inline-flex items-center bg-yellow-100 text-yellow-800 text-xs font-semibold px-3 py-1 rounded-md ring-1">
  //         <span className="h-2 w-2 mr-2 bg-yellow-500 rounded-full"></span>
  //         On Going
  //       </div>
  //     );
  //   case "Finish":
  //     return (
  //       <div className="inline-flex items-center bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-md ring-1 ring-[#2D4E30]">
  //         <FaCheck className="mr-2" />
  //         Finish
  //       </div>
  //     );
  //   default:
  //     return durationBadge;
  // }
};

export const CourseCard = ({ lesson, className }) => {
  const {
    id,
    title,
    contentType,
    contents,
    created_at,
    duration_minutes,
    duration,
    progress,
    questions,
    description,
    user_enrolled,
    course_id,
  } = lesson;
  const { authedPost } = useAuthedApi();
  const [showExamModal, setShowExamModal] = useState(false);
  const navigate = useNavigate();

  const isNew =
    created_at &&
    new Date(created_at) >=
      new Date(new Date().setDate(new Date().getDate() - 7));

  const handleCardClick = async () => {
    if (contentType === "exam") {
      // This logic remains the same
      if (!!progress?.completed) return;
      setShowExamModal(true);
    } else {
      // Logic for non-exam content types
      if (!user_enrolled) {
        try {
          // Use the simplified authedPost to enroll the user
          await authedPost(`/api/courses/${course_id}/enroll/`, {});
        } catch (error) {
          console.error(
            "Enrollment failed:",
            error.response?.data || error.message
          );
          // Optional: show a toast and prevent navigation on failure
          // return;
        }
      }
      // Navigate to the course after successful enrollment or if already enrolled
      navigate(`/learn/subcourse/course/${id}`);
    }
  };

  const handleStartExam = () => {
    setShowExamModal(false);
    navigate(`/learn/exam/${lesson?.id}`);
  };

  const isIncomplete = !progress?.completed;
  const isHighlightNeeded =
    (isNew && !progress) || (contentType === "exam" && isIncomplete);

  const cardStyle = isHighlightNeeded
    ? "bg-red-50 border-dashed border-gray-400"
    : "bg-white border-gray-200";

  const cardClasses = `rounded-lg p-4 transition-all border ${
    progress?.completed && contentType === "exam"
      ? "cursor-default"
      : "cursor-pointer hover:shadow-md"
  } ${cardStyle}`;

  // Define the footer buttons for the dialog
  const dialogFooter = (
    <div className="flex justify-end gap-3 mt-3">
      <button
        onClick={() => setShowExamModal(false)}
        className="px-4 py-2 text-sm font-medium text-[#B3261E] bg-white ring-1 ring-[#B3261E] rounded-lg hover:bg-red-50"
      >
        Next Time
      </button>
      <button
        onClick={handleStartExam}
        className="px-4 py-2 text-sm font-medium text-white bg-indigo-900 rounded-lg hover:bg-indigo-800"
      >
        Start Exam
      </button>
    </div>
  );

  return (
    <>
      <div onClick={handleCardClick} className={cardClasses + " " + className}>
        <div className="flex items-center mb-3">
          <CourseStatus
            status={progress}
            createdAt={created_at}
            duration={duration_minutes || duration}
            contentType={contentType}
          />
        </div>
        <h4 className="font-bold text-gray-800">{title}</h4>
        {contentType === "exam" ? (
          <>
            <p className="text-sm text-gray-600 mt-2">
              Which topics will be covered in the test?
            </p>
            <ul className="list-disc pl-4 mt-1 space-y-1 text-sm text-gray-500">
              <li>{description}</li>
              {/* <li>
                There are few questions related to the module you have studied.
              </li> */}
            </ul>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600 mt-2">What you will learn:</p>
            <ul className="list-disc pl-4 mt-1 space-y-1 text-sm text-gray-500">
              {contents?.slice(0, 3)?.map((concept, index) => (
                <li key={index}>{concept?.title}</li>
              ))}
            </ul>
          </>
        )}
      </div>

      <Dialog
        open={showExamModal}
        onClose={() => setShowExamModal(false)}
        // footer={dialogFooter}
        className="border-none !ring-0"
      >
        <h3 className="text-xl font-bold leading-6 text-gray-800">
          Confirmation
        </h3>
        <p className="text-sm text-gray-700 mt-4 font-normal">
          Please note that you are not allowed to leave the exam page during the
          test. If you leave the page, you will be disqualified.
        </p>
        <div className="flex justify-end gap-3 mt-3">{dialogFooter}</div>
      </Dialog>
    </>
  );
};
